import * as React from 'react';
import { FC, memo } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import Picture from 'components/picture';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';
import { incentiveCardImageDefaultProps } from '../constants';
import { ParsedRewardData } from '../incentive-details/types';

import {
  AddIconButton,
  AlertIcon,
  BackgroundImage,
  CardContainer,
  ImageWrapper,
  InfoContainer,
  InlineAlertContainer,
  InlineAlertSpacer,
  InlineAlertText,
  RewardText,
  StyledClickableContainer,
  StyledRewardLock,
} from './incentive-card.styled';

export interface IIncentiveCard {
  incentiveData: ParsedRewardData;
  handlePress: ILoyaltyRewardsViewProps['handleOnPress'];
  locked?: boolean;
  inlineAlertMessage?: React.ReactNode;
}

export const IncentiveCard: FC<React.PropsWithChildren<IIncentiveCard>> = memo(
  ({ incentiveData, handlePress, locked = false, inlineAlertMessage }) => {
    const { image, imageDescription, name, id, loyaltyEngineId, backgroundImage } = incentiveData;

    // Offers will use the EngineId as the URL identifier because the SanityId might be the same for multiple PersonalizedOffers.
    // Rewards will use the SanityId until we are sure we don't have any deep links to specific Rewards.
    const incentiveSanityId = id;
    const incentiveId = loyaltyEngineId;
    const incentiveName = name;
    const { formatMessage } = useIntl();

    return (
      <CardContainer>
        <StyledClickableContainer
          isLocked={locked || !!inlineAlertMessage}
          onPress={() =>
            !locked ? handlePress(incentiveId, incentiveName, incentiveSanityId) : null
          }
        >
          <InfoContainer>
            {image ? (
              <ImageWrapper>
                {backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
                <Picture image={image} alt={imageDescription} {...incentiveCardImageDefaultProps} />
              </ImageWrapper>
            ) : null}
            <RewardText>{name}</RewardText>
          </InfoContainer>
          {locked ? (
            <StyledRewardLock accessibilityLabel={formatMessage({ id: 'locked' })} />
          ) : (
            <>
              {inlineAlertMessage ? (
                <>
                  <InlineAlertSpacer></InlineAlertSpacer>
                  <InlineAlertContainer>
                    <AlertIcon />
                    <InlineAlertText>{inlineAlertMessage}</InlineAlertText>
                  </InlineAlertContainer>
                </>
              ) : (
                <AddIconButton
                  variant="outline"
                  onPress={() => handlePress(incentiveId, incentiveName, incentiveSanityId)}
                  icon={<Icon variant="add" />}
                  disabled={locked}
                  testID="add-to-cart"
                  accessibilityLabel={formatMessage({ id: 'addToCart' })}
                />
              )}
            </>
          )}
        </StyledClickableContainer>
      </CardContainer>
    );
  }
);
